import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/Reveal";

const goldLogos = [
  { name: "Avery Dennison", src: "/img/avery-dennison.jpg", url: "https://www.averydennison.com/" },
  { name: "Greiner Packaging", src: "/img/greiner.jpg", url: "https://www.greiner-gpi.com/cs" },
  { name: "THIMM", src: "/img/thimm.png", url: "https://www.thimm.com/cz/" },
];

const silverLogos = [
  { name: "DS Smith", src: "/img/ds-smith.jpg", url: "https://www.dssmith.com/" },
  { name: "EKO-KOM", src: "/img/ekokom.png", url: "https://www.ekokom.cz/" },
  { name: "ENVI - PAK", src: "/img/envipak.jpg", url: "https://www.envipak.sk/" },
  { name: "Mondi", src: "/img/mondi.jpg", url: "https://www.mondigroup.com/" },
  { name: "NATUR-PACK", src: "/img/natur-pack.jpg", url: "https://www.naturpack.sk/" },
  { name: "Smurfit Westrock", src: "/img/smurfit-westrock.png", url: "https://www.smurfitkappa.com/cz" },
];

const bronzeLogos = [
  { name: "GS1 Czech Republic", src: "/img/gs1.png", url: "https://www.gs1cz.org/" },
];

const specialLogos = [
  { name: "FACH PACK", src: "/img/fachpack.png", url: "https://www.fachpack.de/en" },
  { name: "ekobal Rožnov", src: "/img/ekobal.png", url: "https://www.ekobal.cz" },
  { name: "PackWall", src: "/img/packwall.png", url: "https://www.packwall.cz" },
];

const coopLogos = [
  { name: "POPAI Central Europe", src: "/img/popai.png", url: "https://www.popai.cz/" },
  { name: "Slovenské združenie pre značkové výrobky", src: "/img/szzv.jpg", url: "https://www.szzv.sk/" },
  { name: "Společnost tisku", src: "/img/spolecnost-tisku.jpg", url: "https://www.spolecnosttisku.cz/" },
  { name: "Svaz výrobců vlnitých lepenek", src: "/img/svvl.jpg", url: "https://www.svvl.cz/" },
  { name: "Zväz obchodu SR", src: "/img/zvaz-obchodu-sr.png", url: "https://www.biznis.sk/" },
];

const mediaLogos = [
  { name: "Gastro & Hotel profi revue", src: "/img/gastro-hotel.png", url: "https://gastroahotel.cz/" },
  { name: "Logistika TV", src: "/img/logistika-tv.png", url: "https://www.logistika.tv/" },
  { name: "LogisticNEWS", src: "/img/logistic-news.jpg", url: "http://www.logisticnews.eu/" },
  { name: "NEXTECH", src: "/img/nextech.png", url: "https://www.nextech.sk/" },
  { name: "Odpad Portal", src: "/img/odpad-portal.png", url: "https://www.odpady-portal.sk/" },
  { name: "Packaging Herald", src: "/img/packaging-herald.jpg", url: "https://www.packagingherald.cz/" },
  { name: "Retail magazin.sk", src: "/img/retail-magazin.jpg", url: "https://www.retailmagazin.sk/" },
  { name: "Retail News", src: "/img/retail-news.png", url: "https://retailnews.cz/" },
  { name: "Svět průmyslu", src: "/img/svet-prumyslu.png", url: "https://www.svetprumyslu.cz/" },
  { name: "Technika a trh", src: "/img/technika-trh.webp", url: "https://www.technikaatrh.cz/" },
  { name: "The Best of Logistics", src: "/img/the-best-of-logistics.jpg", url: "https://logistika.tv/casopis/" },
  { name: "TRANSPORT a LOGISTIKA", src: "/img/transport_a_logistika.jpg", url: "https://transport-logistika.cz/" },
];

const mainMediaLogos = [
  { name: "CS Packaging News", src: "/img/cs-packaging-news.png", url: "https://syba.cz/cs-packaging-news" },
];

const patronageLogos = [
  { name: "Ministerstvo životního prostředí", src: "/img/mzp-logo.png", url: "https://mzp.gov.cz/" },
];

const LogoGrid = ({ items, testPrefix, featured = false }) => (
  <div className={`grid grid-cols-1 gap-6 ${featured ? "max-w-xl" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
    {items.map((logo, i) => (
      <Reveal key={logo.name} delay={0.06 * i}>
        <a
          href={logo.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={logo.name}
          data-testid={`${testPrefix}-${i}`}
          className={`group flex items-center justify-center bg-white p-8 transition-transform duration-300 hover:-translate-y-1 ${featured ? "h-48" : "h-40"}`}
        >
          <img
            src={logo.src}
            alt={logo.name}
            className="max-h-full max-w-full object-contain"
          />
        </a>
      </Reveal>
    ))}
  </div>
);

export const Partners = () => {
  const { t } = useLanguage();
  const p = t.partners;

  return (
    <section id="partners" className="relative py-24 md:py-32 bg-[#010E3A] border-y border-white/5">
      <div className="container-page">
        <Reveal className="max-w-3xl">
          <span className="overline text-[#CEA26B]">{p.overline}</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {p.title}
          </h2>
          {p.subtitle && (
            <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">{p.subtitle}</p>
          )}
          {p.zoneText && (
            <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed border-l-2 border-[#CEA26B] pl-5" data-testid="partners-zone-text">
              {p.zoneText}
            </p>
          )}
        </Reveal>

        <Reveal delay={0.05}>
          <h3
            data-testid="gold-partners-title"
            className="mt-14 font-display font-bold tracking-tight text-[#CEA26B] text-2xl sm:text-3xl"
          >
            {p.goldTitle}
          </h3>
        </Reveal>
        <div className="mt-8">
          <LogoGrid items={goldLogos} testPrefix="gold-partner-logo" />
        </div>

        <Reveal delay={0.05}>
          <h3
            data-testid="silver-partners-title"
            className="mt-16 font-display font-bold tracking-tight text-slate-300 text-2xl sm:text-3xl"
          >
            {p.silverTitle}
          </h3>
        </Reveal>
        <div className="mt-8">
          <LogoGrid items={silverLogos} testPrefix="silver-partner-logo" />
        </div>

        <Reveal delay={0.05}>
          <h3
            data-testid="bronze-partners-title"
            className="mt-16 font-display font-bold tracking-tight text-[#B08D57] text-2xl sm:text-3xl"
          >
            {p.bronzeTitle}
          </h3>
        </Reveal>
        <div className="mt-8">
          <LogoGrid items={bronzeLogos} testPrefix="bronze-partner-logo" />
        </div>

        <Reveal delay={0.05}>
          <h3
            data-testid="special-partners-title"
            className="mt-16 font-display font-bold tracking-tight text-white text-2xl sm:text-3xl"
          >
            {p.specialTitle}
          </h3>
        </Reveal>
        <div className="mt-8">
          <LogoGrid items={specialLogos} testPrefix="special-partner-logo" />
        </div>

        <Reveal delay={0.1}>
          <h3
            data-testid="patronage-title"
            className="mt-20 font-display font-bold tracking-tight text-white text-2xl sm:text-3xl"
          >
            {p.patronageTitle}
          </h3>
        </Reveal>
        <div className="mt-8">
          <LogoGrid items={patronageLogos} testPrefix="patronage-logo" />
        </div>

        <Reveal delay={0.1}>
          <h3
            data-testid="coop-title"
            className="mt-20 font-display font-bold tracking-tight text-white text-2xl sm:text-3xl"
          >
            {p.coopTitle}
          </h3>
        </Reveal>
        <div className="mt-8">
          <LogoGrid items={coopLogos} testPrefix="coop-logo" />
        </div>

        <Reveal delay={0.1}>
          <h3
            data-testid="main-media-partner-title"
            className="mt-20 font-display font-bold tracking-tight text-[#CEA26B] text-2xl sm:text-3xl"
          >
            {p.mainMediaTitle}
          </h3>
        </Reveal>
        <div className="mt-8">
          <LogoGrid items={mainMediaLogos} testPrefix="main-media-partner-logo" featured />
        </div>

        <Reveal delay={0.1}>
          <h3
            data-testid="media-partners-title"
            className="mt-20 font-display font-bold tracking-tight text-white text-2xl sm:text-3xl"
          >
            {p.mediaTitle}
          </h3>
        </Reveal>
        <div className="mt-8">
          <LogoGrid items={mediaLogos} testPrefix="media-partner-logo" />
        </div>
      </div>
    </section>
  );
};
